/* eslint-disable no-param-reassign */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useLayoutEffect, useRef } from "react";
import DefaultAxios, { AxiosRequestConfig } from "axios";
import { useDebouncedCallback } from "use-debounce";
import axios from "../AxiosInstance";
import { clearTokens, refreshToken } from "./helpers";
import useNotification from "../components/notifications/hook";
import { NotificationTypes } from "../components/notifications/notification-types";
import AccessControlService from "../services/domain/AccessControlService";
import LogService from "../services/LogService";

const clientId = process.env.REACT_APP_AD_CLIENTID;
const TOKEN_EXPIRED_ERROR_CODE = 401;
const ACCESS_DENIED_ERROR_CODE = 403;

interface InterceptorProps {
  getAccessToken: () => string;
  startLoginProcess: () => void;
  onAccessDeniedError: () => void;
  onConcurrentLimitReached: () => void;
}

// Define the structure of a retry queue item
interface RetryQueueItem {
  // biome-ignore lint/suspicious/noExplicitAny: Using any to satisfy refreshAndRetryQueue typescript error
  resolve: (value?: any) => void;
  reject: (error?: unknown) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue
let refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

function Interceptors({
  getAccessToken,
  startLoginProcess,
  onAccessDeniedError,
  onConcurrentLimitReached,
}: InterceptorProps): JSX.Element {
  const notification = useNotification();

  // Debounce the login process to prevent multiple login requests
  const debouncedStartLogin = useDebouncedCallback(startLoginProcess, 2000);
  const startLoginProcessRef = useRef(debouncedStartLogin);
  startLoginProcessRef.current = debouncedStartLogin;

  useLayoutEffect(
    () => {
      const clearTokensIfNotRefreshTokenEnvSet = () => {
        LogService.log("Spawning login process");
        clearTokens();
        startLoginProcessRef.current?.();
      };

      /**
       * Attempt to refresh the access token.
       * On success, saves the new token to
       * localStorage and redirects user to their destination.
       *
       * Error suggests the refresh token has expired.
       * In that case, clear all tokens from localStorage
       * and redirect user to login page.
       */
      const tokenRefresh = async () => {
        const token = refreshToken();
        if (token !== "" && clientId) {
          return AccessControlService.acquireAccessToken(clientId, token);
        }

        if (token === "") {
          // If there is no refresh token, it makes sense to start a new login process
          startLoginProcessRef.current?.();
        }
        return null;
      };

      /**
       * If token is found, return the token in a Bearer string
       * Otherwise return placeholder string
       */
      const bearerToken = () => `Bearer ${getAccessToken()}`;

      /**
       * Set default Content-Type and Authorization headers
       */
      const requestInterceptorId = axios.interceptors.request.use((req) => {
        req.headers["Content-Type"] = "application/json";
        req.headers.credentials = "include";
        req.headers.authorization = bearerToken();

        return req;
      });

      /**
       * React to status code TOKEN_EXPIRED_ERROR_CODE, when access token has expired,
       * and trigger attempt to refresh it
       */
      const responseInterceptorId = axios.interceptors.response.use(
        (res) => res,
        async (error) => {
          if (!error.response || !DefaultAxios.isAxiosError(error)) {
            return Promise.reject(error);
          }

          const originalRequest = error.config;

          // If there is no token, no need to proceed further
          if (bearerToken() === "") {
            return Promise.reject(error);
          }

          const { status } = error.response;

          // Case when web user token has expired.
          if (status === TOKEN_EXPIRED_ERROR_CODE && originalRequest) {
            if (!isRefreshing) {
              try {
                LogService.log(
                  `Received error ${TOKEN_EXPIRED_ERROR_CODE}, attempting refresh token`
                );
                isRefreshing = true;
                notification.createNotification({
                  type: NotificationTypes.Snackbar,
                  message:
                    "Session expired. Attempting to refresh the session.",
                  severity: "warning",
                });
                const tokens = await tokenRefresh();
                if (!tokens) {
                  throw new Error("No tokens returned from refresh");
                }
                const configWithNewToken = {
                  ...originalRequest,
                  headers: {
                    ...originalRequest.headers,
                    authorization: `Bearer ${tokens.accessToken}`, // axios normalize "Authorization" to "authorization"
                  },
                };

                // Retry all requests in the queue with the new token
                refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
                  DefaultAxios.request({
                    ...config,
                    headers: {
                      ...config.headers,
                      authorization: `Bearer ${tokens.accessToken}`,
                    },
                  })
                    .then((response) => resolve(response))
                    .catch((err) => reject(err));
                });

                // Clear the queue
                refreshAndRetryQueue = [];

                // Retry the original request
                return DefaultAxios(configWithNewToken);
              } catch (refreshError) {
                // Handle token refresh error
                clearTokensIfNotRefreshTokenEnvSet();
                // Reject all requests in the queue
                refreshAndRetryQueue.forEach(({ reject }) => {
                  reject(refreshError);
                });
                // Clear the queue
                refreshAndRetryQueue = [];

                return Promise.reject(error);
              } finally {
                isRefreshing = false;
              }
            }

            // Add the original request to the queue
            return new Promise<void>((resolve, reject) => {
              refreshAndRetryQueue.push({
                config: originalRequest,
                resolve,
                reject,
              });
            });
          }

          if (
            status === ACCESS_DENIED_ERROR_CODE &&
            error.response &&
            error.response.data.detail ===
              "Maximum concurrent login session limit exceeded"
          ) {
            LogService.log(
              `Received error ${ACCESS_DENIED_ERROR_CODE} concurrent login limit reached, attempting to show access denied`
            );
            onConcurrentLimitReached();
          } else if (status === ACCESS_DENIED_ERROR_CODE) {
            LogService.log(
              `Received error ${ACCESS_DENIED_ERROR_CODE}, attempting to show access denied`
            );
            onAccessDeniedError();
          }

          return Promise.reject(error);
        }
      );
      return () => {
        axios.interceptors.request.eject(requestInterceptorId);
        axios.interceptors.response.eject(responseInterceptorId);
      };
    },
    // event handler should be memoized to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // notification, // This is actually a static value, useNotification is not a real hook
      // getAccessToken, // This is a function that get token from storage, implementation should not depend on closure
      // onAccessDeniedError,
      // onConcurrentLimitReached,
    ]
  );

  return <></>;
}

export default Interceptors;
